import { useState } from "react";
import { useTranslation } from "react-i18next";

const DatePicker = () => {
    const { t } = useTranslation()
    const today = new Date();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hoverDate, setHoverDate] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
    const [calendarYear, setCalendarYear] = useState(today.getFullYear());

    const formatDate = (date) => {
        if (!date) return "MM/DD/YYYY";
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    };

    const selectToday = () => {
        setStartDate(today);
        setEndDate(today);
        setShowCalendar(false);
    };

    const selectYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        setShowCalendar(false);
    };

    const selectLast7Days = () => {
        const lastWeek = new Date();
        lastWeek.setDate(today.getDate() - 6);
        setStartDate(lastWeek);
        setEndDate(today);
        setShowCalendar(false);
    };

    const selectThisMonth = () => {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDate(firstDay);
        setEndDate(today);
        setShowCalendar(false);
    };

    // Generate calendar days
    const getDaysInMonth = (month, year) => {
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const days = [];

        const startDay = firstDayOfMonth.getDay();

        for (let i = 0; i < startDay; i++) {
            days.push(null);
        }

        for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };
    const selectDate = (date) => {
        if (!startDate || (startDate && endDate)) {
            setStartDate(date);
            setEndDate(null);
            setHoverDate(null);
        } else {
            if (date < startDate) {
                setEndDate(startDate); // Swap start and end if selecting in reverse
                setStartDate(date);
            } else {
                setEndDate(date);
            }
            setShowCalendar(false);
            setHoverDate(null);
        }
    };

    // Check if a date is between startDate and endDate (for highlighting)
    const isBetweenRange = (day) => {
        if (!startDate || !endDate) return false;
        return day >= startDate && day <= endDate;
    };

    const prevMonth = () => {
        if (calendarMonth === 0) {
            setCalendarMonth(11);
            setCalendarYear(calendarYear - 1);
        } else {
            setCalendarMonth(calendarMonth - 1);
        }
    };

    const nextMonth = () => {
        if (calendarMonth === 11) {
            setCalendarMonth(0);
            setCalendarYear(calendarYear + 1);
        } else {
            setCalendarMonth(calendarMonth + 1);
        }
    };

    return (
        <div className="relative inline-block">
            <button
                className="px-4 py-2 border rounded-md bg-white text-black"
                onClick={() => {
                    if (!showCalendar) {
                        // If opening, set the calendar to the selected month or today
                        if (startDate) {
                            setCalendarMonth(startDate.getMonth());
                            setCalendarYear(startDate.getFullYear());
                        } else {
                            setCalendarMonth(today.getMonth());
                            setCalendarYear(today.getFullYear());
                        }
                    }
                    setShowCalendar(!showCalendar);
                }}
            >
                {formatDate(startDate)} - {formatDate(endDate)}
            </button>


            {showCalendar && (
                <div className="absolute top-12 left-0 lg:left-[-80px] sm:w-[351px] bg-white text-black shadow-lg rounded-lg p-4 flex space-x-4 w-[470px]">
                    {/* Quick Selection (Left Side) */}
                    <div className="flex flex-col space-y-2 border-r border-gray-700 pr-4 w-[150px] xs:w-[100px]    ">
                        <button onClick={selectToday} className="text-left xxs:text-sm hover:bg-bluelight p-1 rounded">
                            {t("today")}
                        </button>
                        <button onClick={selectYesterday} className="text-left xxs:text-sm hover:bg-bluelight p-1 rounded">
                            {t("yesterday")}
                        </button>
                        <button onClick={selectLast7Days} className="text-left xxs:text-sm hover:bg-bluelight p-1 rounded">
                            {t("last7Days")}
                        </button>
                        <button onClick={selectThisMonth} className="text-left xxs:text-sm hover:bg-bluelight p-1 rounded">
                            {t("thisMonth")}
                        </button>
                    </div>

                    {/* Calendar (Right Side) */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <div>
                                {/* <span>
                                    {new Date(calendarYear, calendarMonth).toLocaleString("default", {
                                        month: "long",
                                    })}{" "}
                                    {calendarYear}
                                </span> */}
                                <span>
                                    {t(`months.${calendarMonth}`)} {calendarYear}
                                </span>
                            </div>
                            <div className="flex justify-center items-center gap-2">
                                <button onClick={prevMonth} className="border  rounded-full size-8">&lt;</button>
                                <button onClick={nextMonth} className="border  rounded-full size-8">&gt;</button>
                            </div>

                        </div>

                        <div className="grid grid-cols-7 gap-1 text-center text-gray-400 mb-2">
                            <span>Su</span>
                            <span>Mo</span>
                            <span>Tu</span>
                            <span>We</span>
                            <span>Th</span>
                            <span>Fr</span>
                            <span>Sa</span>
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                            {getDaysInMonth(calendarMonth, calendarYear).map((day, index) => (
                                <button
                                    key={index}
                                    onClick={() => day && selectDate(day)}
                                    onMouseEnter={() => {
                                        if (day && startDate && !endDate) {
                                            setHoverDate(day);
                                        }
                                    }}

                                    className={`p-2 py-1 lg:py-0.5 md:text-sm flex justify-center items-center rounded-full
                                        ${day && startDate && day.toDateString() === startDate.toDateString()
                                            ? "bg-darkblue text-white" // Start date
                                            : endDate && day && day.toDateString() === endDate.toDateString()
                                                ? "bg-darkblue text-white" // End date
                                                : isBetweenRange(day) || (startDate && !endDate && hoverDate &&
                                                    ((day > startDate && day < hoverDate) || (day < startDate && day > hoverDate)))
                                                    ? "bg-bluelight text-black" // Hover range effect
                                                    : "hover:bg-gray-200"
                                        }`}

                                >
                                    {day ? day.getDate() : ""}
                                </button>

                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
